/**
 * @file src/components/blocks/cookie-banner.block.jsx
 * @description
 * GDPR-compliant cookie consent banner.
 * Appears once per browser session until the user accepts or declines.
 * Stores preference in localStorage under 'moksy_cookie_consent'.
 * Values: 'accepted' | 'declined'
 *
 * ⚠️ A CONSENT BELONGS TO A PERSON, NOT TO A BROWSER (GC-078).
 *
 * Walked 2026-09-11: a consultant signed in on a machine somebody else had
 * used, and the choice the previous visitor made at this banner was pushed up
 * to HER account as her consent to marketing. She had never seen the banner.
 * That is not consent under any reading of the word, and "the localStorage
 * said so" is not a lawful basis.
 *
 * So the stored record carries WHOSE decision it is. A signed-in person adopts
 * a local record only when they are the one who made it; a guest's decision
 * governs that guest's session and is never promoted to an account. Signing
 * out clears it, so the next person at this keyboard is asked for themselves.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { identity } from '../../api/identity';
import { storage } from '../../utils/storage';
import { useAuthContext } from '../../context/AuthContext';

import { Button } from '../elements/Button.jsx';
// Dispatched by the footer's "Cookie settings" control (RF-002: the banner
// had no way back once dismissed). The banner is the only place consent is
// read or written, so reopening it — rather than building a second settings
// surface — keeps that true.
export const OPEN_COOKIE_SETTINGS_EVENT = 'moksy:open-cookie-settings';
const CONSENT_KEY = storage.KEYS.COOKIE_CONSENT;
const CONSENT_VERSION = '2026-03-17';

// Who a stored decision belongs to. A signed-in person is their own id; a
// visitor who has not signed in is `guest`, and a guest decision is never
// anybody's account consent.
const GUEST = 'guest';
const subjectOf = (user) => String(user?.userId || user?.id || '') || GUEST;

const defaultConsent = () => ({
  status: 'declined',
  version: CONSENT_VERSION,
  source: 'banner',
  subject: GUEST,
  updatedAt: new Date().toISOString(),
  categories: {
    essential: true,
    functional: false,
    analytics: false,
    marketing: false
  }
});

// Treat a "decision-shaped" consent as real consent, anything else as
// absent. The previous version returned a stub for ANY truthy object,
// which is wrong: when a fresh user authenticates, identity.getSettings()
// returns `{ consent: {} }` as a placeholder. The stub then overwrote
// the freshly-saved localStorage value (with `version: ''`), the
// version-mismatch check fired, and the banner reappeared on every
// auth state change.
//
// We now require BOTH a recognised status AND a non-empty version
// string before accepting the value as a real decision. Empty objects,
// missing status, or missing version → null (treated as "no decision
// recorded yet").
const normalizeConsent = (value) => {
  if (!value || typeof value !== 'object') return null;
  const status = ['accepted', 'declined', 'customized'].includes(value.status) ? value.status : null;
  const version = String(value.version || '').trim();
  if (!status || !version) return null;
  return {
    status,
    version,
    source: ['banner', 'settings', 'migration'].includes(value.source) ? value.source : 'banner',
    // Records written before this field existed have no subject. They are
    // treated as a guest's — unattributable, so never adopted by an account.
    subject: String(value.subject || GUEST),
    updatedAt: String(value.updatedAt || ''),
    categories: {
      essential: true,
      functional: !!value?.categories?.functional,
      analytics: !!value?.categories?.analytics,
      marketing: !!value?.categories?.marketing
    }
  };
};

// `subject` is LOCAL bookkeeping, not part of the decision. It records whose
// record this is, so a guest's choice is never adopted by an account
// (GC-078). The server never needed it: PATCH /settings is authenticated and
// already knows who is asking. SettingsPatchSchema here and ConsentSchema in
// the users service are both `.strict()` with no `subject` key, and
// User.settings.consent has no field for it — so sending the stored record
// wholesale threw `ZodError: unrecognized_keys` before the request ever left
// the browser. Send the decision; keep the owner here.
const wireConsent = ({ subject: _localOnly, ...decision } = {}) => decision;

export const CookieBanner = () => {
  const { isAuthenticated, loading, user } = useAuthContext();
  const subject = subjectOf(user);
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [mode, setMode] = useState('summary');
  const [saving, setSaving] = useState(false);
  const [consent, setConsent] = useState(defaultConsent());
  const declineRef = useRef(null);
  const dialogRef = useFocusTrap({
    active: visible,
    initialFocusRef: declineRef
  });

  useEffect(() => {
    if (loading) return;

    let cancelled = false;

    const syncConsent = async () => {
      const localConsent = normalizeConsent(storage.get(CONSENT_KEY));

      if (isAuthenticated) {
        try {
          const settings = await identity.getSettings();
          if (cancelled) return;
          const serverConsent = normalizeConsent(settings?.consent);

          // 🔒 Only THIS person's own local decision is theirs to adopt.
          // Anything left on the device by somebody else — including a guest
          // decision made before signing in — is discarded here, and the
          // banner asks them for themselves. One extra click, against
          // recording a stranger's answer as their consent (GC-078).
          const mine = localConsent && localConsent.subject === subject ? localConsent : null;

          // Pick the most-recent decision between the person's own local
          // record and the server's. A fresh sign-up has no server-side
          // consent yet, so their own local decision is pushed up rather than
          // being overwritten by an empty placeholder.
          const localTime = mine?.updatedAt ? Date.parse(mine.updatedAt) : 0;
          const serverTime = serverConsent?.updatedAt ? Date.parse(serverConsent.updatedAt) : 0;
          const newerIsLocal = !!mine && (!serverConsent || localTime >= serverTime);
          const activeConsent = newerIsLocal ? mine : (serverConsent || mine);

          if (activeConsent) {
            // Only write to storage when the picked value is actually
            // different from what's stored — otherwise we'd thrash
            // localStorage on every auth-state change with the same data.
            const stored = storage.get(CONSENT_KEY);
            const same = stored && stored.version === activeConsent.version
              && stored.status === activeConsent.status
              && stored.updatedAt === activeConsent.updatedAt;
            if (!same) storage.set(CONSENT_KEY, activeConsent);
            setConsent(activeConsent);
            setVisible(activeConsent.version !== CONSENT_VERSION);
            // Mirror local → server when local is the newer (or only)
            // record. Fire-and-forget; never blocks the UI.
            if (newerIsLocal && (!serverConsent || serverTime < localTime)) {
              void identity.patchSettings({ consent: wireConsent(activeConsent) }).catch(() => {});
            }
            return;
          }
        } catch {
          // fall through to local-only behavior
        }
      }

      // Signed out: a guest's own decision governs the guest session. A
      // record belonging to a named person is not this visitor's, so it is
      // ignored rather than shown back to them.
      if (localConsent && localConsent.subject === subject) {
        setConsent(localConsent);
        setVisible(localConsent.version !== CONSENT_VERSION);
        return;
      }

      setConsent(defaultConsent());
      setVisible(true);
    };

    void syncConsent();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, loading, subject]);

  // Reopen on request, regardless of version: this is the one way back once
  // the banner has been dismissed (RF-002). Starts on the detail view, since
  // somebody asking for "settings" wants the categories, not the summary.
  useEffect(() => {
    const reopen = () => {
      setMode('customize');
      setVisible(true);
    };
    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, reopen);
    return () => window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, reopen);
  }, []);

  const persistConsent = async (consentGiven) => {
    // Stamped with who gave it, here, so there is exactly one place a
    // decision can be written without an owner (GC-078).
    const nextConsent = { ...consentGiven, subject };
    storage.set(CONSENT_KEY, nextConsent);
    setConsent(nextConsent);
    if (isAuthenticated) {
      // A failure here must not trap the person behind the banner. Their
      // decision is already in local storage and stamped with their id, so
      // syncConsent mirrors it up on the next load (local is the newer
      // record). Leaving the await uncaught is what turned one rejected
      // request into a consent dialog that could never be dismissed.
      try {
        await identity.patchSettings({ consent: wireConsent(nextConsent) });
      } catch {
        // Recorded locally; retried on next sync.
      }
    }
    setVisible(false);
    setMode('summary');
  };

  const handleAccept = async () => {
    const next = {
      status: 'accepted',
      version: CONSENT_VERSION,
      source: 'banner',
      updatedAt: new Date().toISOString(),
      categories: {
        essential: true,
        functional: true,
        analytics: true,
        marketing: true
      }
    };
    setSaving(true);
    try { await persistConsent(next); } finally { setSaving(false); }
  };

  const handleDecline = async () => {
    const next = {
      ...defaultConsent(),
      updatedAt: new Date().toISOString()
    };
    setSaving(true);
    try { await persistConsent(next); } finally { setSaving(false); }
  };

  const handleSaveCustom = async () => {
    const hasOptional = consent.categories.functional || consent.categories.analytics || consent.categories.marketing;
    const next = {
      ...consent,
      status: hasOptional ? 'customized' : 'declined',
      version: CONSENT_VERSION,
      source: 'settings',
      updatedAt: new Date().toISOString(),
      categories: {
        essential: true,
        functional: !!consent.categories.functional,
        analytics: !!consent.categories.analytics,
        marketing: !!consent.categories.marketing
      }
    };
    setSaving(true);
    try { await persistConsent(next); } finally { setSaving(false); }
  };

  if (!visible) return null;

  // On the interview the composer lives at the bottom of the screen, and the
  // banner sat on top of it: the first thing a guest had to do to answer a
  // question was dismiss a cookie notice (audit MAI-IX-29). There it docks
  // under the header instead; everywhere else it stays at the foot.
  const composerRoute = /^\/(guest\/)?requirements\/v2\//.test(location.pathname);
  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label="Cookie consent"
      aria-live="polite"
      className={composerRoute
        ? 'fixed top-14 left-0 right-0 z-50 px-4 pt-3 sm:px-6'
        : 'fixed bottom-0 left-0 right-0 z-50 px-4 pb-4 sm:px-6 sm:pb-6'}
    >
      <div className="mx-auto max-w-4xl rounded-2xl border border-gray-200 bg-background shadow-2xl p-5 flex flex-col gap-4">
        <div className="text-sm text-gray-700 leading-relaxed">
          <span className="font-semibold text-neutral">We use cookies</span> to keep you signed in and improve your experience.
          {/* RF-003: this used to say "by continuing to use Moksy, you agree"
              right beside the granular choices below it — implying that just
              being on the page was consent, which contradicts an explicit
              choice being required at all. Consent comes only from one of
              the three buttons; this line just explains and links to what
              they're choosing between. */}
          {' '}Choose below, or read our{' '}
          <Link to="/legal/cookies" className="text-primary underline font-medium hover:opacity-80 transition-opacity">
            Cookie Policy
          </Link>
          {' '}and{' '}
          <Link to="/legal/privacy" className="text-primary underline font-medium hover:opacity-80 transition-opacity">
            Privacy Policy
          </Link>.
        </div>

        {mode === 'customize' && (
          <div className="grid gap-3 rounded-xl border border-gray-100 bg-gray-50 p-4">
            <label className="flex items-center justify-between gap-4">
              <span className="text-sm font-medium text-gray-800">Strictly Necessary</span>
              <input type="checkbox" checked readOnly disabled />
            </label>
            <label className="flex items-center justify-between gap-4">
              <span className="text-sm font-medium text-gray-800">Functional Preferences</span>
              <input
                type="checkbox"
                checked={consent.categories.functional}
                onChange={(e) => setConsent({
                  ...consent,
                  categories: { ...consent.categories, functional: e.target.checked }
                })}
              />
            </label>
            <label className="flex items-center justify-between gap-4">
              <span className="text-sm font-medium text-gray-800">Analytics</span>
              <input
                type="checkbox"
                checked={consent.categories.analytics}
                onChange={(e) => setConsent({
                  ...consent,
                  categories: { ...consent.categories, analytics: e.target.checked }
                })}
              />
            </label>
            <label className="flex items-center justify-between gap-4">
              <span className="text-sm font-medium text-gray-800">Marketing</span>
              <input
                type="checkbox"
                checked={consent.categories.marketing}
                onChange={(e) => setConsent({
                  ...consent,
                  categories: { ...consent.categories, marketing: e.target.checked }
                })}
              />
            </label>
          </div>
        )}

        <div className="flex flex-wrap gap-2 flex-shrink-0">
          {/* "Essential Only" (decline) carries the same visual weight as
              "Accept All" — equal prominence keeps consent a genuine choice
              rather than nudging toward acceptance. */}
          <Button size="sm" pill={false}
            ref={declineRef}
            disabled={saving}
            onClick={handleDecline}>
            Essential Only
          </Button>
          {mode === 'customize' ? (
            <Button variant="outline" size="sm" pill={false}
              disabled={saving}
              onClick={handleSaveCustom}>
              Save Preferences
            </Button>
          ) : (
            <Button variant="outline" size="sm" pill={false}
              disabled={saving}
              onClick={() => setMode('customize')}>
              Customise
            </Button>
          )}
          <Button size="sm" pill={false}
            disabled={saving}
            onClick={handleAccept}>
            Accept All
          </Button>
        </div>
      </div>
    </div>
  );
};
